import { toName } from '@/helpers/UrlHelper'
import { useRouter } from 'next/router'
import Breadcrumb from './Breadcrumb'
import Container from './Container'
import NeedHelp from './NeedHelp'
import WaveDivider from './WaveDivider'
import {
  Currency,
  FromPrice,
  Grid,
  Highlight,
  Highlights,
  Left,
  Price,
  PricingContainer,
  PricingWrapper,
  RatingScore,
  ReviewContainer,
  ReviewCounter,
  Right,
  StickyContainer,
  PricingButtons,
  WaveContainer,
  MoreToExplore,
  TopSectionWrapper,
  MobileBookNow,
  TitleDecorators,
  SeeMore,
  NZRiskDisclosureText,
} from './GalleryProductLayout.styles'
import TitleDecorator from './TitleDecorator'
import ProductGallery from './ProductGallery'
import ProductReviews from './ProductReviews'
import Recommended from './Recommended'
import Sustainability from './Sustainability'
import Location from './Location'
import {
  Spacing,
  Heading,
  Accordion,
  Afterpay,
  Button,
  HighlightedReviews,
  ReviewStars,
} from '@big-red-group/storefront-common-ui'
import Accessibility from './Accessibility'
import { useState, useEffect, useContext, useRef } from 'react'
import HotDealMessage from './HotDealMessage'
import {
  CreateProductSessionShownEventInput,
  PdpResponse,
  ProductRecommendation,
  SelectedSessionWithVariant,
  StaticAvailability,
  UserBookingSelections,
  Voucher,
} from '@/types'
import ContentBlock from './ContentBlock'
import { processMoreInfo } from '@/helpers/MoreInfoHelper'
import { getRetailPrice } from '@/helpers/PriceHelper'
import { useProductViewed, useProductAdded } from '@/hooks/AnalyticsProductHook'
import { DEFAULT_CURRENCY_CODE, EDITING_FLAG } from '@/constant/segment'
import { useElementClick } from '@/hooks/HTMLElementClickHook'
import { HighlightGraphic, UnderlineGraphic } from '@/components/Icons.styles'
import Icon from '@/components/Icons'
import Head from 'next/head'
import { constructFullUrl } from '@/helpers/ClientSideRenderHelper'
import ProductPoints from './ProductPoints'
import {
  getPoints,
  getPointsBadgeIcon,
  getPointsBadgeStyles,
  getPointsDescription,
  getPointsStyles,
  getRedeemPoints,
} from '@/helpers/LoyaltyCampaignHelper'
import ProductPointsBadge from './ProductPointsBadge'
import { useProductSessionChecked, useProductSessionShown } from '@/hooks/AnalyticsProductHook'
import { useImageHook } from '@/hooks/AnalyticsImageHook'
import { getHotDeals } from '@/helpers/BookingHelper'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import { renderOpenGraph } from '@/helpers/PageTitleHelper'
import { isClient } from '@/helpers/ClientSideRenderHelper'
import { scrollToElement } from '@/helpers/PageScrollHelper'
import { setRecentlyViewed } from '@/helpers/ClientSideRenderHelper'
import { getIndexTag } from '@/helpers/SeoHelper'
import ProductDisclaimer from '@/components/ProductDisclaimer'
import { isChristmas } from '@/helpers/DateHelper'
import { useTheme } from 'styled-components'
import { AccordionLabel } from './AccordionLabel.styles'
import { formatCurrency } from '@big-red-group/storefront-common-checkout'
import NZRiskDisclosure from '@/components/NZRiskDisclosureStatement'
import ProductBooking from '@/components/ProductBooking'
import { BookingContext } from '@/context/BookingContext'
import InfoBadge from '@/components/InfoBadge'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import { createCanonicalLink } from '@/helpers/UrlHelper'
import ShareButtons from './ShareButtons'
import { RichSnippetContext } from '@/context/RichSnippetContext'

// TODO content block title decoration
const GalleryProductLayout: React.FC<
  Pick<
    PdpResponse,
    | 'productDetails'
    | 'productRecommendations'
    | 'productReviews'
    | 'moreToExplore'
    | 'agentConfiguration'
    | 'loyaltyCampaign'
    | 'lastMinutePricingDates'
  > & {
    canonicalLink?: string
    images: string[]
    staticAvailability: StaticAvailability | {}
  }
> = ({
  productDetails,
  productRecommendations,
  productReviews,
  moreToExplore,
  agentConfiguration,
  loyaltyCampaign,
  lastMinutePricingDates,
  images,
  staticAvailability,
}) => {
  const trackImageEvent = useImageHook(productDetails.operatorName)
  const trackProductAdded = useProductAdded()
  const trackProductSessionChecked = useProductSessionChecked(productDetails)
  const trackProductSessionShown = useProductSessionShown(productDetails)
  const trackButtonClicked = useElementClick('Button')

  const router = useRouter()
  const linkClick = useElementClick('Link')
  const theme = useTheme()
  const { useRedemptionLayout } = useContext(BookingContext)

  const { lang, part1: region } = router.query
  const isProductEdit = EDITING_FLAG in router.query ? true : false
  const {
    id,
    operatorName,
    name,
    fromPrice,
    productSessions,
    highlights,
    helpCode,
    description,
    moreInformation,
    geolocations,
    canonicalRegionUrlSegment,
    urlSegment,
    handlerKey: productHandlerKey,
    title,
    summary,
    decorators,
    cancellationTerm,
    accessibility,
    timezone,
  } = productDetails

  const { handlerKey } = agentConfiguration
  const trackProductViewed = useProductViewed()
  const defaultProperties = useContext(DefaultPropertiesContext)
  const allowGiveAsGift = Boolean(
    defaultProperties?.agentConfiguration?.allowGiveProductAsGift &&
      !useRedemptionLayout &&
      productDetails.allowsExperienceVoucherPurchase
  )
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const isBookingEngine = defaultProperties?.agentConfiguration?.isBookingEngine
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent
  const productLayout = defaultProperties?.agentConfiguration?.productLayout
  const displayAfterPay = defaultProperties?.agentConfiguration?.paymentOptions?.some(
    (option) => option.type === 'AFTERPAY'
  )!!
  const isGiftCardProduct = productHandlerKey === 'exozGiftCard2'
  const { total: reviewsTotal, average, reviews } = productReviews

  const [userBookingSelections, setUserBooking] = useState<UserBookingSelections>({})
  const [itemUuid, setItemUuid] = useState<string | undefined>(undefined)
  const [isNZRiskModalOpen, setIsNZRiskModalOpen] = useState(false)
  const [activeBookingTab, setActiveBookingTab] = useState<0 | 1>(0)
  const canonicalGroupUrlSegment = productDetails?.canonicalGroupUrlSegment

  const updateUserBooking = (moreSelections: UserBookingSelections) => {
    setUserBooking({ ...userBookingSelections, ...moreSelections })
  }
  const handleClickReviews = () => {
    scrollToElement('product-reviews', 115)
  }

  const handleClickBookNow = (tab: 0 | 1, title: string, event: any) => {
    setActiveBookingTab(tab)
    scrollToElement('booking')
    trackButtonClicked({ location: `PDP | ${title}`, event })
  }

  const [showAllDecorators, setShowAllDecorators] = useState(false)
  const toggleAllDecorators = () => {
    setShowAllDecorators(true)
  }

  const renderTitleDecorators = () => {
    if (!decorators) return

    return (
      <TitleDecorators open={showAllDecorators}>
        {featuredHotDeal && <TitleDecorator icon="flame" label="Hot deal" color="var(--sale)" />}
        {decorators.map(({ id, label, name }) => (
          <TitleDecorator key={id} decoratorKey={name} label={label} />
        ))}
        {!showAllDecorators && decorators.length > 4 && <SeeMore onClick={toggleAllDecorators}>See more...</SeeMore>}
      </TitleDecorators>
    )
  }

  useEffect(() => {
    setRecentlyViewed(productDetails)
  }, [productDetails])

  // TODO: Add campaign data when implemented and whether this view contributes to a recommendation
  useEffect(() => {
    trackProductViewed(productDetails, isProductEdit, productLayout, featuredHotDeal, decorators)
  }, [trackProductViewed, productDetails, isProductEdit, productLayout])

  const addProductToCart = (isGift: boolean, selectedVariants?: SelectedSessionWithVariant[], voucher?: Voucher) => {
    if (selectedVariants?.length) {
      for (const selectedVariant of selectedVariants) {
        const { option: variant, quantity, ...session } = selectedVariant

        trackProductAdded({
          product: productDetails,
          url: window.location.href,
          isGift,
          quantity,
          isProductEdit,
          session,
          variant,
          voucher,
          filteredDecorators: decorators,
        })
      }
      return
    }

    trackProductAdded({
      product: productDetails,
      url: window.location.href,
      isGift,
      quantity: 1,
      isProductEdit,
      voucher,
      filteredDecorators: decorators,
    })
  }

  const renderProductPoints = () => {
    if (!loyaltyCampaign?.pointsPerDollar) return null

    const { containerStyle, descriptionStyle, pointsStyle, separatorStyle } = getPointsStyles(handlerKey)

    return (
      <ProductPoints
        points={getRedeemPoints(fromPrice)!}
        description={getPointsDescription(handlerKey)}
        containerStyle={containerStyle}
        descriptionStyle={descriptionStyle}
        pointsStyle={pointsStyle}
        separatorStyle={separatorStyle}
      />
    )
  }
  // Trigger Product Session Checked event
  const sessionChecked = (
    sessionFrom: Date,
    sessionTo: Date,
    bookingCalendarType?: string,
    hasHotDeal?: boolean,
    hasLastMinutePricing?: boolean
  ) => {
    trackProductSessionChecked({
      sessionFrom,
      sessionTo,
      isProductEdit,
      bookingCalendarType,
      hasHotDeal,
      hasLastMinutePricing,
    })
  }

  // Trigger Product Session Shown event
  const sessionShown = (eventInput: Omit<CreateProductSessionShownEventInput, 'product' | 'url'>) => {
    eventInput = { ...eventInput, isProductEdit }
    trackProductSessionShown(eventInput)
  }

  const renderPricing = (renderAfterpay: boolean) => {
    // retailPrice exists and is > than fromPrice (I know)
    return (
      <div>
        <PricingWrapper onSale={retailPrice != fromPrice}>
          <FromPrice>
            From {retailPrice && retailPrice != fromPrice && <span>{formatCurrency(retailPrice)}</span>}
          </FromPrice>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <Price data-testid="product-price">{formatCurrency(fromPrice)}</Price>
            <Currency data-testid="total-price">
              {defaultProperties?.agentConfiguration?.currencyCode ?? 'AUD'}
            </Currency>
            {renderProductPoints()}
          </div>
        </PricingWrapper>
        <Spacing size="XXS" />
        {renderAfterpay && <Afterpay amount={fromPrice} installment="4" />}
      </div>
    )
  }

  const needToKnowContent = processMoreInfo(moreInformation)
  // TODO
  // const accessibilityBlock = contentBlocks?.find((content) => content.id === 'ACCESSIBILITY')
  const hasReviews = reviewsTotal > 0
  const hasRecommendations = productRecommendations?.length > 0
  const reviewsRating = average ? average.toFixed(1) : undefined
  const retailPrice = getRetailPrice(productSessions)
  // TODO rating
  const moreToExploreFormatted: ProductRecommendation[] = moreToExplore?.map(
    ({
      id: productId,
      operatorName,
      name: productName,
      canonicalRegionUrlSegment: regionUrlSegment,
      urlSegment: productUrlSegment,
      images,
      fromPrice,
      rating,
    }) => ({ productId, operatorName, productName, regionUrlSegment, productUrlSegment, images, fromPrice, rating })
  )

  const [visibleReviews, setVisibleReviews] = useState(3)

  const loadMoreReviews = () => {
    setVisibleReviews((prevVisibleReviews) => prevVisibleReviews + 3)
  }

  const mobileStickyPos = useRef<HTMLDivElement>(null)
  const [scrolledPastPos, setScrolledPastPos] = useState(false)
  useEffect(() => {
    if (window.innerWidth < 768) {
      window.addEventListener('scroll', () => {
        if (mobileStickyPos.current != null) {
          setScrolledPastPos(window.scrollY > mobileStickyPos.current.offsetTop)
        }
      })
    }
  }, [])

  // TODO: OMS
  // useEffect(() => {
  //   const itemUUID = editProductUUID ? editProductUUID : ''
  //   if (!itemUUID) return
  //   const editItem = cart?.items?.find((item) => item.uuid === itemUUID)
  //   if (editItem) {
  //     updateUserBooking({
  //       previouslySelectedOptions: loadPreviouslySelectedOptions(productSessions ?? [], editItem),
  //       previouslySelectedDate: new Date(editItem.bookingDate!),
  //       previouslySelectedSession: editItem.metadata?.sessionId,
  //       previouslySelectedPickup: editItem.pickup,
  //       previouslySelectedGroupId: loadPreviouslySelectedGroupId(editItem),
  //       isEdit: isProductEdit,
  //     })
  //     setItemUuid(editItem.uuid)
  //     setEditCartItem(editItem)
  //   }
  // }, [])

  const featuredHotDeal = getHotDeals(productSessions)

  const getBookingButtonText = () => {
    return useRedemptionLayout ? 'Book with your voucher' : 'Book now'
  }

  const renderBookingComponent = () => {
    return (
      <ProductBooking
        productDetails={productDetails}
        userBookingSelections={userBookingSelections}
        onUserSelection={updateUserBooking}
        onAddToCart={addProductToCart}
        uuid={itemUuid}
        isEdit={isProductEdit}
        retailPrice={retailPrice}
        onBookNowClick={trackButtonClicked}
        onSessionChecked={sessionChecked}
        onSessionShown={sessionShown}
        activeBookingTab={activeBookingTab}
        showGiftingTab={allowGiveAsGift}
        lastMinutePricingDates={lastMinutePricingDates}
        staticAvailability={staticAvailability}
      />
    )
  }

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()

  collect({
    type: 'Product',
    data: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: productDetails.name,
      image: productDetails.images?.[0],
      description: productDetails.description,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: reviewsRating,
        reviewCount: reviewsTotal,
        bestRating: 5,
        worstRating: 1,
      },
      offers: {
        '@type': 'Offer',
        price: `${fromPrice || retailPrice}`,
        priceCurrency: DEFAULT_CURRENCY_CODE,
        priceValidUntil: '2030-12-31',
        availability: 'InStock',
      },
      reviews: reviews?.map((r) => ({
        '@type': 'Review',
        headline: r.summary,
        author: {
          '@type': 'Person',
          name: r.author,
        },
        datePublished: r.date,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
        reviewBody: r.blurb,
      })),
    },
  })

  return (
    <div style={{ position: 'relative' }} className="product-page">
      <Head>
        <link
          rel="canonical"
          href={createCanonicalLink(`${canonicalRegionUrlSegment}/${canonicalGroupUrlSegment || urlSegment}`)}
        />
        <title>{title}</title>
        <meta name="description" property="og:description" content={summary} />
        {getIndexTag(isBrgAgent, 'pdp', handlerKey)}
        {renderOpenGraph(title, summary, images && images[0])}
      </Head>
      <>
        {!isBookingEngine && (
          <WaveContainer>
            <WaveDivider fill="#FFFFFF" curveSize="medium" style={{ height: '50%' }} />
          </WaveContainer>
        )}
        {/* position relative here and in the Wave component are to push Wave to background */}
        <Container style={{ position: 'relative', zIndex: 1 }}>
          {!isBookingEngine && (
            <TopSectionWrapper>
              <Breadcrumb
                items={[
                  { text: 'Home', href: `${baseUrl}/${lang}` },
                  { text: toName(region as string), href: `${baseUrl}/${lang}/${region}` },
                ]}
              />
              <ShareButtons
                url={
                  isClient() ? constructFullUrl(`${baseUrl}/${lang}/${canonicalRegionUrlSegment}/${urlSegment}`) : ''
                }
              />
            </TopSectionWrapper>
          )}
          <Heading size="XXL" as="h1" dataTestId="product-name">
            {name}
          </Heading>
          {renderTitleDecorators()}
          {/* TODO tour & svg */}
          {/* {tour && (
          <TourInfo>
            <TourRegion>
              {tour.time} · {tour.regionUrl && tour.regionName && <Link href={tour.regionUrl}>{tour.regionName}</Link>}
            </TourRegion>
            {tour.badges && tour.badges.length > 0 && (
              <TourBadges>
                {tour.badges.map((badge) => (
                  <TourBadge key={badge}>
                    {badge}
                  </TourBadge>
                ))}
              </TourBadges>
            )}
          </TourInfo>
        )} */}
          <ProductGallery images={images} trackImageEvent={trackImageEvent} />
          <Spacing size="XL" />
          <Grid>
            <Left>
              {hasReviews && (
                <>
                  <ReviewContainer>
                    <ReviewStars rating={+reviewsRating!} />
                    <RatingScore>{reviewsRating}</RatingScore>
                    <ReviewCounter onClick={handleClickReviews}>
                      {reviewsTotal} review{reviewsTotal > 1 ? 's' : ''}
                    </ReviewCounter>
                  </ReviewContainer>
                  <Spacing size="XXL" />
                </>
              )}
              {highlights && (
                <>
                  <Heading size="L" dataTestId="product-highlights">
                    Highlights & inclusions
                    <HighlightGraphic>
                      <Icon name="highlight" width={26} height={24} />
                    </HighlightGraphic>
                  </Heading>
                  <Highlights>
                    {highlights?.map((hl) => (
                      <Highlight key={hl}>
                        <Icon
                          name="circleTick"
                          width={47}
                          height={47}
                          stroke={theme?.colourPalette?.primaryColour || '#FCC703'}
                        />
                        {hl}
                      </Highlight>
                    ))}
                  </Highlights>
                  <Spacing size="XL" />
                </>
              )}
              {/* TODO: Product region should not be determined by timezone, */}
              {timezone === 'NZ' && (
                <div>
                  <NZRiskDisclosureText onClick={() => setIsNZRiskModalOpen(true)}>
                    Read Risk Disclosure Statement
                  </NZRiskDisclosureText>
                  <NZRiskDisclosure isOpen={isNZRiskModalOpen} onClose={() => setIsNZRiskModalOpen(false)} />
                </div>
              )}
              <Spacing size="XL" />
            </Left>
            <Right>
              <StickyContainer>
                {!useRedemptionLayout && (
                  <>
                    {featuredHotDeal && (
                      <>
                        <Spacing size="M" />
                        <HotDealMessage message={featuredHotDeal} />
                      </>
                    )}
                    <PricingContainer>
                      {renderPricing(displayAfterPay)}
                      {/* TODO */}
                      {/* <FinancialDescriptors items={financialDescriptors} /> */}
                    </PricingContainer>
                    {loyaltyCampaign?.pointsPerDollar && (
                      <div style={{ marginTop: '8px' }}>
                        <ProductPointsBadge
                          icon={getPointsBadgeIcon(handlerKey)}
                          points={getPoints(fromPrice, loyaltyCampaign)!}
                          description={getPointsDescription(handlerKey)}
                          style={getPointsBadgeStyles(handlerKey)}
                        />
                      </div>
                    )}
                  </>
                )}
                <Spacing size="M" />
                {useRedemptionLayout && (
                  <>
                    <InfoBadge
                      heading="You're all set!"
                      subText="Your voucher is ready to use for this experience"
                      iconName="gift"
                      backgroundColor="#e9ebff"
                      borderColor="#394BF1"
                      textColor="brand-secondary"
                    />
                    <Spacing size="XS" />
                  </>
                )}
                <PricingButtons>
                  <Button
                    size="M"
                    onClick={(event) => handleClickBookNow(0, getBookingButtonText(), event)}
                    dataTestId="book-now"
                  >
                    {getBookingButtonText()}
                  </Button>
                  {allowGiveAsGift && (
                    <Button
                      size="M"
                      inverse
                      dataTestId="give-as-gift"
                      christmasBtn={isChristmas() && isBrgAgent}
                      onClick={(event) => {
                        handleClickBookNow(1, 'Gift it', event)
                      }}
                    >
                      Gift it
                    </Button>
                  )}
                </PricingButtons>
                {cancellationTerm && (
                  <>
                    <Spacing size="M" />
                    <ProductDisclaimer cancellationPolicy={cancellationTerm} />
                  </>
                )}
              </StickyContainer>
            </Right>
          </Grid>
          <Left>
            {!isGiftCardProduct && (
              <>
                {renderBookingComponent()}
                <Spacing size="S" />
              </>
            )}
            <div ref={mobileStickyPos}></div>
            {reviews && reviews.length >= 5 && (
              <>
                <HighlightedReviews
                  productName={name}
                  average={+reviewsRating!}
                  total={reviewsTotal}
                  reviews={reviews}
                  onViewAllReviews={() => scrollToElement('product-reviews', 115)}
                  HeadingComponent={() => (
                    <Heading size="L" dataTestId="product-details">
                      Highlighted reviews
                      <UnderlineGraphic>
                        <Icon name="underline" width={76} height={7} fill="#FAC532" />
                      </UnderlineGraphic>
                    </Heading>
                  )}
                />
                <Spacing size="XXL" />
              </>
            )}
            <Heading size="L" dataTestId="product-details">
              What you can expect
              {!isBookingEngine && (
                <UnderlineGraphic>
                  <Icon name="underline" width={76} height={7} fill="#FAC532" />
                </UnderlineGraphic>
              )}
            </Heading>
            <ContentBlock content={`<p>${description}</p>`} style={'whiteSpace: pre-line'} />
            {geolocations && (
              <Location
                latitude={geolocations[0].latitude}
                longitude={geolocations[0].longitude}
                address={geolocations[0].address}
                dataTestId="product-location"
              />
            )}
            <div mcp-id="product-details"></div>
            <Heading size="L">
              What you need to know
              {!isBookingEngine && (
                <UnderlineGraphic>
                  <Icon name="underline" width={76} height={7} fill="#FAC532" />
                </UnderlineGraphic>
              )}
            </Heading>
            {needToKnowContent.map(({ heading, content }, index) => (
              <Accordion key={index} openOnLoad={index == 0} content={content} anchorColor="brand-secondary">
                <AccordionLabel>{heading}</AccordionLabel>
              </Accordion>
            ))}
            <Spacing size="XXXL" />
            {accessibility && (
              <>
                <Heading size="L" headingGraphic="stroke">
                  Accessibility
                </Heading>
                <Accessibility content={accessibility} productId={id} />
                <Spacing size="XXL" />
              </>
            )}
            {hasReviews && (
              <ProductReviews
                average={+reviewsRating!}
                reviews={reviews}
                loadedReviews={reviews?.slice(0, visibleReviews) || []}
                total={reviewsTotal}
              />
            )}
            {reviews?.length > visibleReviews && (
              <Button alternate dataTestId="load-reviews" onClick={loadMoreReviews}>
                Load More
              </Button>
            )}
            <Spacing size="XXXL" />
            {hasRecommendations && !isBookingEngine && (
              <>
                <LazyLoadComponent>
                  <Recommended
                    title="You might also like"
                    products={productRecommendations}
                    dataTestId="product-suggestions"
                  />
                </LazyLoadComponent>
                <Spacing size="XXXL" />
              </>
            )}
            {isBrgAgent && <NeedHelp helpCode={helpCode} supplierName={operatorName} dataTestId="product-help" />}
            <Spacing size="XXXL" />
            {isBrgAgent && (
              <LazyLoadComponent>
                <Sustainability />
              </LazyLoadComponent>
            )}
          </Left>
        </Container>
        <MobileBookNow active={scrolledPastPos} data-testid="mobile-book-bar">
          <Button size="L" style={{ width: '100%' }} onClick={() => scrollToElement('booking')}>
            {`${productHandlerKey !== 'exozGiftCard2' ? 'Book now' : 'Buy now'}`}
          </Button>
          {featuredHotDeal && <HotDealMessage message={featuredHotDeal} />}
        </MobileBookNow>
        {moreToExploreFormatted?.length > 0 && !isBookingEngine && (
          <MoreToExplore>
            <Container>
              <LazyLoadComponent>
                <Recommended title="More to explore" products={moreToExploreFormatted} />
              </LazyLoadComponent>
            </Container>
            <Spacing size="XL" />
          </MoreToExplore>
        )}
      </>
    </div>
  )
}

export default GalleryProductLayout
